import type { ActivitySlashCommand } from './activity-slash-command';

/**
 * Реестр команд чата Activity.
 * Хранит команды в singleton-инстансе и поддерживает upsert по `id`
 * (это важно для корректной работы в dev/HMR).
 */
class ActivityChatCommandRegistry {
  private static instance: ActivityChatCommandRegistry | null = null;

  private readonly commandsById = new Map<string, ActivitySlashCommand>();

  static getInstance(): ActivityChatCommandRegistry {
    if (!ActivityChatCommandRegistry.instance) {
      ActivityChatCommandRegistry.instance = new ActivityChatCommandRegistry();
    }
    return ActivityChatCommandRegistry.instance;
  }

  /**
   * Регистрирует команду или обновляет существующую с тем же id.
   */
  register(command: ActivitySlashCommand): void {
    this.commandsById.set(command.id, command);
  }

  /**
   * Возвращает актуальный список команд в порядке регистрации.
   */
  getCommands(): ActivitySlashCommand[] {
    return Array.from(this.commandsById.values());
  }
}

export const activityChatCommandRegistry = ActivityChatCommandRegistry.getInstance();

/** Возвращает зарегистрированные команды Activity-чата. */
export function getActivityChatCommands(): ActivitySlashCommand[] {
  return activityChatCommandRegistry.getCommands();
}

/** Возвращает команду по id (slug) или null, если команда не зарегистрирована. */
export function getActivityChatCommandById(commandId: string): ActivitySlashCommand | null {
  const normalized = commandId?.trim();
  if (!normalized) return null;
  return getActivityChatCommands().find(command => command.id === normalized) ?? null;
}

type ActivityCommentCommand = ActivitySlashCommand & {
  comment: {
    label?: string;
  };
};

function isActivityCommentCommand(
  command: ActivitySlashCommand
): command is ActivityCommentCommand {
  return Boolean(command.comment);
}

/** Возвращает только команды, доступные для запуска из меню комментария. */
export function getActivityCommentCommands(): ActivityCommentCommand[] {
  return getActivityChatCommands().filter(isActivityCommentCommand);
}
